<template>
  <div class="pay-detail-section pay-detail-grid" :class="gridClass">
    <a-divider class="detail-divider">
      <a-icon type="file-protect" /> {{ $t("decaissement.paymentMethod.traite") }}
    </a-divider>
    <div class="detail-columns-header">
      <div class="column-header idx-col">#</div>
      <div class="column-header">{{ $t("decaissement.traiteNum") }}</div>
      <div class="column-header">{{ $t("fournisseur.dueDate") }}</div>
      <div v-if="!hideBankColumn" class="column-header">{{ $t("decaissement.chequeBank") }}</div>
      <div v-if="showRib" class="column-header">{{ $t("decaissement.traiteRib") }}</div>
      <div class="column-header">{{ $t("recette.montant") }}</div>
      <div class="column-header actions-col"></div>
    </div>
    <div class="detail-items">
      <div
        v-for="(item, index) in rows"
        :key="index"
        class="detail-row"
        :class="{ 'detail-row--alt': index % 2 === 0 }"
      >
        <div class="detail-cell idx-col"><span class="row-num">{{ index + 1 }}</span></div>
        <div class="detail-cell">
          <a-input
            :value="item.num"
            size="small"
            :placeholder="$t('decaissement.placeholderTraiteNum')"
            :class="{ 'input-error': isInvalid(index, 'num') }"
            @input="e => onNumInput(index, e.target.value)"
          />
        </div>
        <div class="detail-cell">
          <a-date-picker
            :value="item.dateEchance"
            size="small"
            style="width: 100%"
            format="DD/MM/YYYY"
            :placeholder="$t('decaissement.placeholderDueDate')"
            :class="{ 'input-error': isInvalid(index, 'dateEchance') }"
            @change="d => onUpdate(index, 'dateEchance', d)"
          />
        </div>
        <div v-if="!hideBankColumn" class="detail-cell">
          <bank-select
            :value="item.bank"
            size="small"
            :placeholder="$t('decaissement.placeholderBank')"
            @change="v => onUpdate(index, 'bank', v)"
          />
        </div>
        <div v-if="showRib" class="detail-cell">
          <a-input
            :value="item.rib"
            size="small"
            maxlength="20"
            :placeholder="$t('decaissement.placeholderRib')"
            :class="{ 'input-error': isInvalid(index, 'rib') }"
            @input="e => onRibInput(index, e.target.value)"
          />
        </div>
        <div class="detail-cell">
          <a-input-number
            :value="item.montant"
            size="small"
            style="width: 100%"
            :min="0"
            :precision="3"
            :placeholder="$t('decaissement.placeholderAmount')"
            :class="{ 'input-error': isInvalid(index, 'montant') }"
            @change="v => onUpdate(index, 'montant', v)"
          />
        </div>
        <div class="detail-cell actions-col">
          <a-button v-if="index > 0" type="danger" icon="delete" size="small" @click="onRemove(index)" />
        </div>
      </div>
    </div>
    <a-button type="dashed" block icon="plus" class="mt-2" @click="onAdd">
      {{ $t("decaissement.addTraite") }}
    </a-button>
    <div class="sum-bar mt-2" :class="{ match: sumMatch, mismatch: !sumMatch }">
      <span>{{ $t("recette.montant") }}: {{ sum.toFixed(3) }} / {{ totalRequired.toFixed(3) }}</span>
      <a-tag :color="sumMatch ? 'green' : 'red'">{{ sumMatch ? "OK" : $t("dette.sumMismatch") }}</a-tag>
    </div>
  </div>
</template>

<script>
import BankSelect from "@/components/BankSelect.vue";

const EMPTY = { num: null, dateEchance: null, bank: undefined, montant: 0 };

export default {
  name: "PayTraiteSection",
  components: { BankSelect },
  props: {
    rows: { type: Array, required: true },
    totalRequired: { type: Number, default: 0 },
    hideBankColumn: { type: Boolean, default: false },
    showRib: { type: Boolean, default: false },
  },
  data() {
    return { errors: [] };
  },
  computed: {
    gridClass() {
      if (this.showRib) {
        return this.hideBankColumn
          ? "pay-detail-grid--cheque-no-bank"
          : "pay-detail-grid--cheque-full";
      }
      return this.hideBankColumn
        ? "pay-detail-grid--traite-no-bank"
        : "pay-detail-grid--traite-full";
    },
    sum() { return this.rows.reduce((s, r) => s + (Number(r.montant) || 0), 0); },
    sumMatch() { return Math.abs(this.sum - this.totalRequired) < 0.01; },
  },
  methods: {
    onUpdate(i, f, v) {
      this.$emit("update", i, f, v);
      this.validate(i);
    },
    onNumInput(i, v) { this.$emit("update", i, "num", v.replace(/[^0-9]/g, "")); },
    onRibInput(i, v) {
      const cleaned = v.replace(/[^0-9]/g, "").slice(0, 20);
      this.$emit("update", i, "rib", cleaned);
      this.validate(i);
    },
    onAdd() {
      if (this.rows.length && !this.validate(this.rows.length - 1)) return;
      this.$emit("add", { ...EMPTY });
    },
    onRemove(i) {
      this.$emit("remove", i);
      this.errors.splice(i, 1);
    },
    validate(i) {
      const r = this.rows[i];
      if (!r) return true;
      const e = {};
      if (!r.num || !/^[0-9]+$/.test(r.num)) e.num = true;
      if (!r.dateEchance) e.dateEchance = true;
      if (this.showRib && (!r.rib || r.rib.length !== 20 || !/^[0-9]+$/.test(r.rib))) e.rib = true;
      if (!r.montant || r.montant <= 0) e.montant = true;
      this.$set(this.errors, i, e);
      return Object.keys(e).length === 0;
    },
    validateAll() {
      let ok = true;
      this.rows.forEach((_, i) => { if (!this.validate(i)) ok = false; });
      return ok && this.sumMatch;
    },
    isInvalid(i, f) { return this.errors[i] && this.errors[i][f]; },
  },
};
</script>

<style scoped>
@import "./payDetailSection.css";
</style>
